import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../lib/api'
import { farmerApi, offerStatusLabel, type Offer } from '../lib/farmer'
import { money, num, shortDate } from '../lib/format'
import { Banner, Empty, Loading, Panel, Pill } from '../ui/kit'
import { FarmerHeader } from './FarmerShell'

export function MyHarvests() {
  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)

  function load() {
    setLoading(true)
    farmerApi
      .get<Offer[]>('/portal/offers')
      .then(setOffers)
      .catch(() => setOffers([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  async function withdraw(o: Offer) {
    if (!window.confirm(`Cancel your offer of ${num(o.estimatedKg, 0)} kg of ${o.cropName}?`)) return
    try {
      await farmerApi.post(`/portal/offers/${o.id}/withdraw`, { reason: 'Cancelled by farmer' })
      setNote({ tone: 'ok', text: 'That harvest was cancelled.' })
      load()
    } catch (e) {
      setNote({ tone: 'crit', text: e instanceof ApiError ? e.message : 'Could not cancel that.' })
    }
  }

  const live = offers.filter((o) => ['open', 'accepted', 'scheduled'].includes(o.status))
  const past = offers.filter((o) => !['open', 'accepted', 'scheduled'].includes(o.status))

  return (
    <>
      <FarmerHeader />
      <div className="field-greet">
        <h1>My harvests</h1>
        <p>What you have offered us, and what happened to it.</p>
      </div>

      <div className="field-body">
        {note && (
          <Banner tone={note.tone === 'ok' ? 'info' : 'crit'} title={note.text}>{null}</Banner>
        )}

        {loading && <Panel><Loading rows={4} /></Panel>}

        {!loading && offers.length === 0 && (
          <Empty title="You have not posted anything yet" action={
            <Link className="btn" to="/f/post">Post a harvest</Link>
          }>
            Tell us what you will have and when, and we will come and collect it.
          </Empty>
        )}

        {live.length > 0 && (
          <Panel title="Waiting" meta={`${live.length}`}>
            <div className="panel-body col" style={{ gap: 'var(--sp-6)' }}>
              {live.map((o) => (
                <OfferCard key={o.id} offer={o} onWithdraw={() => void withdraw(o)} />
              ))}
            </div>
          </Panel>
        )}

        {past.length > 0 && (
          <Panel title="Finished" meta={`${past.length}`}>
            <div className="panel-body col" style={{ gap: 'var(--sp-6)' }}>
              {past.map((o) => <OfferCard key={o.id} offer={o} />)}
            </div>
          </Panel>
        )}
      </div>
    </>
  )
}

function OfferCard({ offer, onWithdraw }: { offer: Offer; onWithdraw?: () => void }) {
  const s = offerStatusLabel(offer.status)
  const collected = offer.collectedKg != null
  const short = collected && Number(offer.collectedKg) < Number(offer.estimatedKg)

  return (
    <div style={{
      border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
      padding: 'var(--sp-6) var(--sp-7)', background: 'var(--surface)',
    }}>
      <div className="spread" style={{ alignItems: 'flex-start' }}>
        <div>
          <b style={{ fontSize: 'var(--fs-lg)' }}>{offer.cropName}</b>
          <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
            <span className="mono">{offer.code}</span> · posted {shortDate(offer.createdAt)}
          </div>
        </div>
        <Pill tone={s.tone} dot={false}>{s.text}</Pill>
      </div>

      <div className="mini-row" style={{ marginTop: 'var(--sp-6)' }}>
        <div className="mini">
          <i>You offered</i>
          <b className="num">{num(offer.estimatedKg, 0)} kg</b>
          <span>ready {shortDate(offer.readyFrom)}</span>
        </div>
        {collected ? (
          <div className="mini">
            <i>We weighed</i>
            <b className="num">{num(offer.collectedKg, 1)} kg</b>
            <span>{offer.fulfilmentPct}% of what you offered</span>
          </div>
        ) : (
          <div className="mini">
            <i>Worth about</i>
            <b className="num">{money(offer.indicativeValue, { currency: false })}</b>
            <span>at {money(offer.indicativePricePerKg, { currency: false })}/kg</span>
          </div>
        )}
      </div>

      {offer.status === 'scheduled' && offer.scheduledFor && (
        <p style={{ fontSize: 'var(--fs-sm)', marginTop: 'var(--sp-5)', color: 'var(--ok)' }}>
          We are coming on <b>{shortDate(offer.scheduledFor)}</b>. Have it ready.
        </p>
      )}

      {offer.status === 'declined' && offer.declinedReason && (
        <p style={{ fontSize: 'var(--fs-sm)', marginTop: 'var(--sp-5)', color: 'var(--ink-2)' }}>
          {offer.declinedReason}
        </p>
      )}

      {short && (
        <p className="hint" style={{ marginTop: 'var(--sp-5)' }}>
          You are paid for what the scale weighed, not for the estimate. Lot{' '}
          <span className="mono">{offer.lotCode}</span>.
        </p>
      )}

      {onWithdraw && offer.status !== 'scheduled' && (
        <button
          className="btn quiet block"
          style={{ marginTop: 'var(--sp-6)', minHeight: 38 }}
          onClick={onWithdraw}
        >
          Cancel this
        </button>
      )}
    </div>
  )
}
